import { ProductRelation } from "../types/types";

/**
 * Collapses repeated relation targets, keeping the first occurrence (EB-522).
 *
 * Curated data carried the same related-product id more than once on 11 active products — 17
 * duplicate entries. The storefront rendered the repeated card twice, and because
 * `RelatedProducts` keys its list on `relation.id`, duplicate ids also meant **duplicate React
 * keys**: a reconciliation hazard invisible in production, since production builds strip React's
 * development warnings.
 *
 * The stored rows were cleaned up on 2026-07-27, so this is defence rather than a live fix — the
 * render path should not depend on the data being well-formed.
 *
 * First occurrence rather than `unique_by`-style sorting: the order is curated, and the admin
 * chose it. Deduping must not quietly reorder the cards.
 */
export const dedupeRelations = (relations: ProductRelation[] | undefined): ProductRelation[] => {
  if (!relations?.length) return [];

  const seen = new Set<string>();
  const out: ProductRelation[] = [];

  for (const relation of relations) {
    if (!relation?.id || seen.has(relation.id)) continue;
    seen.add(relation.id);
    out.push(relation);
  }

  return out;
};

/** Whether `id` is already related, so the editor can refuse to add it twice. */
export const isAlreadyRelated = (relations: ProductRelation[], id: string): boolean =>
  relations.some((relation) => relation.id === id);
